/**
 * useSelectionManager
 *
 * Purpose:
 * - Provides direct access to the app's single SelectionManager instance.
 * - Keeps components decoupled from the full AppStore API while preserving MobX reactivity.
 *
 * Why this hook (and not a global/singleton)?
 * - Ensures we always read the SelectionManager from the same provider-backed AppStore instance.
 * - Works reliably with React Fast Refresh, tests, and potential multi-root setups.
 *
 * Migration guidance:
 * - Replace `appStore.selectionManager` at call sites with:
 *     const selectionManager = useSelectionManager();
 *     selectionManager.setScenario(...);
 *     const project = selectionManager.project;
 *
 * If we later want stronger decoupling, we can introduce a dedicated
 * SelectionManagerContext and have this hook read from it instead.
 */
import type { SelectionManager } from "@/types/SelectionManager";
import useAppStore from "@hooks/useAppStore";

/**
 * Returns the current SelectionManager instance from the AppStore.
 *
 * Example:
 *   const selectionManager = useSelectionManager();
 *   selectionManager.setModel(model);
 *   console.log(selectionManager.scenario?.name);
 */
export default function useSelectionManager(): SelectionManager {
  return useAppStore().selectionManager;
}


